import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@material-ui/core'
import cls from 'classnames'
import { useEffect } from 'react'
import { Controller, FieldErrors, FieldPath } from 'react-hook-form'

import { transformUnderscoreInSpaceAndUppercaseFirstLetter } from '@/utils/format-strings'

import {
  AllNewReportForm,
  DefaultNewReportForm,
} from '../../new-report-form-schema'
import {
  caneOptions,
  soybeanOptions,
  useNewReportForm,
} from '../../use-new-report-form'
import { useStyles } from './styles'

interface WeedClassSelectorInputProps {
  type: 'cane' | 'soybean'
}

export function WeedClassSelectorInput({ type }: WeedClassSelectorInputProps) {
  const classes = useStyles()

  const { control, setValue, formState, watch } = useNewReportForm()

  const errors = formState.errors as FieldErrors<AllNewReportForm>

  const [cane, soybean, caneOptionsValues, soybeanOptionsValues] = watch([
    'cane',
    'soybean',
    'cane_options',
    'soybean_options',
  ])

  const types = {
    cane: {
      title: type === 'cane' && 'cana',
      error: errors.cane_options?.root,
      value: cane,
      enabled: type === 'cane' && cane,
      optionsValues: caneOptionsValues,
      defaultOptions: caneOptions,
      name: 'cane_options',
      options: Object.keys(caneOptions).map((option) => ({
        option,
        name: `cane_options.${option as keyof typeof caneOptions}` as const,
      })),
    },
    soybean: {
      title: type === 'soybean' && 'soja',
      error: errors.soybean_options?.root,
      value: soybean,
      enabled: type === 'soybean' && soybean,
      optionsValues: soybeanOptionsValues,
      defaultOptions: soybeanOptions,
      name: 'soybean_options',
      options: Object.keys(soybeanOptions).map(
        (option) =>
          ({
            option,
            name: `soybean_options.${option as keyof typeof soybeanOptions}`,
          } as const),
      ),
    },
  } as const

  const current = types[type]

  function clearOptionsOnCultureChange(
    name: 'cane_options' | 'soybean_options',
    options: typeof current.defaultOptions,
  ) {
    Object.keys(options).forEach((key) => {
      setValue(`${name}.${key}` as FieldPath<DefaultNewReportForm>, false)
    })
  }

  useEffect(() => {
    if (!current.value) {
      clearOptionsOnCultureChange(current.name, current.defaultOptions)
    }
  }, [current.value])

  return (
    <>
      <Box
        className={
          current.error
            ? cls(classes.formGroup, classes.borderError)
            : cls(classes.formGroup, classes.border)
        }
        style={{
          opacity: current.enabled ? 1 : 0.5,
        }}
      >
        <Typography variant='body1'>
          Selecione as classes de <strong>{current.title} daninhas</strong> que
          deseja no relatório:
        </Typography>
        <FormGroup row>
          <FormControlLabel
            defaultChecked={false}
            control={
              <Checkbox
                size='small'
                checked={Object.values(current.optionsValues).every(Boolean)}
                onChange={(e) => {
                  const newValue = Object.keys(current.defaultOptions).reduce(
                    (acc, key) => {
                      acc[key as keyof typeof current.defaultOptions] =
                        e.target.checked
                      return acc
                    },
                    current.defaultOptions,
                  )

                  setValue(current.name, newValue)
                }}
              />
            }
            label={<Typography variant='subtitle1'>Todas classes</Typography>}
            disabled={!current.enabled}
          />

          {current.options.map(({ option, name }) => (
            <Controller
              key={option}
              control={control}
              name={name}
              defaultValue={false}
              render={({ field: { value, onChange, name } }) => (
                <FormControlLabel
                  name={name}
                  disabled={!current.enabled}
                  control={
                    <Checkbox
                      size='small'
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant='subtitle1'>
                      {transformUnderscoreInSpaceAndUppercaseFirstLetter(
                        option,
                      )}
                    </Typography>
                  }
                />
              )}
            />
          ))}
        </FormGroup>
      </Box>
      {current.error && (
        <Typography variant='subtitle2' className={classes.errorMessage}>
          * {current.error.message}
        </Typography>
      )}
    </>
  )
}
